"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getTodaySpecialHours } from '@/lib/hours-utils';

interface BusinessHoursCollapsibleProps {
  businessHours?: any;
  isRetailStore?: boolean;
  /** Tighter rows with single-line times — for narrow sidebar columns. */
  compact?: boolean;
}

function formatTime(time24: string): string {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  const mmStr = m.toString().padStart(2, "0");
  return `${hour12}:${mmStr} ${period}`;
}

function getTimezoneLabel(timezone?: string): string {
  if (!timezone) return "";
  try {
    const tzAbbrev = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value;
    return tzAbbrev || timezone;
  } catch (e) {
    return timezone;
  }
}

export default function BusinessHoursCollapsible({
  businessHours,
  isRetailStore = true,
  compact = false
}: BusinessHoursCollapsibleProps) {
  const [isExpanded, setIsExpanded] = useState(true); // Changed to true for open by default
  const tz = businessHours?.timezone;
  const tzLabel = getTimezoneLabel(tz);

  if (!businessHours) return null;

  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden ${compact ? '' : 'mb-5'}`}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Store Hours
          </h2>
          <span className="text-sm text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-3 py-1 rounded-full">
            Weekly schedule
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
        )}
      </button>

      {/* Collapsible Content */}
      {isExpanded && isRetailStore && (
        <div className={`border-t border-neutral-200 dark:border-neutral-700 ${compact ? 'px-3 pb-3' : 'px-4 pb-4'}`}>
          <div className={`bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 ${compact ? 'p-3' : 'p-6'}`}>
            <div className={`grid grid-cols-1 ${compact ? 'gap-2' : 'gap-3'}`}>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }) === day;
                const dayAbbr = day.slice(0, 3);

                // Check if we have periods format (multiple periods per day)
                const hasPeriodsFormat = businessHours?.periods && Array.isArray(businessHours.periods);
                let timeSlots: { open: string; close: string }[] = [];
                let hasHours = false;

                if (hasPeriodsFormat) {
                  // New format: periods array
                  const dayUpper = day.toUpperCase();
                  const dayPeriods = businessHours.periods.filter((p: any) => p.day === dayUpper);
                  hasHours = dayPeriods.length > 0;
                  if (hasHours) {
                    timeSlots = dayPeriods.map((period: any) => ({
                      open: formatTime(period.open),
                      close: formatTime(period.close),
                    }));
                  }
                } else {
                  // Old format: single period per day
                  const dayHours = businessHours?.[day];
                  hasHours = !!dayHours;
                  if (hasHours) {
                    timeSlots = [{
                      open: formatTime(dayHours.open),
                      close: formatTime(dayHours.close),
                    }];
                  }
                }

                return (
                  <div
                    key={day}
                    className={`flex items-start justify-between rounded-lg border gap-4 ${compact ? 'p-2.5' : 'p-4'} ${
                      isToday
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`font-semibold text-sm ${isToday ? 'text-blue-900 dark:text-blue-100' : 'text-neutral-900 dark:text-neutral-100'} min-w-[40px]`}>
                        {dayAbbr}
                      </span>
                      {isToday && <span className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full font-medium whitespace-nowrap">Today</span>}
                    </div>
                    <div className={`font-medium flex-shrink-0 text-right text-sm ${
                      isToday
                        ? 'text-blue-700 dark:text-blue-300'
                        : hasHours ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-500 dark:text-neutral-400'
                    }`}>
                      {hasHours ? (
                        <span className="whitespace-nowrap">
                          {timeSlots.map(slot => `${slot.open} – ${slot.close}`).join(', ')}
                          {tzLabel && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-1.5">{tzLabel}</span>
                          )}
                        </span>
                      ) : (
                        <span>Closed</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Special Hours - Today & Upcoming */}
            {(() => {
              const specialHours = getTodaySpecialHours(businessHours);
              if (specialHours.length === 0) return null;

              const todayHours = specialHours.filter(sh => sh.label === 'today');
              const upcomingHours = specialHours.filter(sh => sh.label === 'upcoming');

              const formatTime = (time24: string): string => {
                if (!time24) return "";
                const [h, m] = time24.split(":").map(Number);
                const period = h >= 12 ? "PM" : "AM";
                const hour12 = h % 12 || 12;
                const mmStr = m.toString().padStart(2, "0");
                
                // Add timezone abbreviation if timezone is available
                if (businessHours?.timezone) {
                  try {
                    const tzAbbrev = new Intl.DateTimeFormat('en-US', { 
                      timeZone: businessHours.timezone, 
                      timeZoneName: 'short' 
                    }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value;
                    return `${hour12}:${mmStr} ${period} ${tzAbbrev || businessHours.timezone}`;
                  } catch (e) {
                    // Fallback to timezone string if abbreviation fails
                    return `${hour12}:${mmStr} ${period} ${businessHours.timezone}`;
                  }
                }
                
                return `${hour12}:${mmStr} ${period}`;
              };

              const formatDate = (dateStr: string): string => {
                const date = new Date(dateStr + 'T00:00:00');
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              };

              return (
                <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Special Hours
                  </h3>
                  <div className="space-y-3">
                    {/* Today's Special Hours */}
                    {todayHours.map((sh, idx) => (
                      <div key={`today-${sh.date}-${idx}`} className="flex flex-col gap-2 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-amber-900 dark:text-amber-100 flex-shrink-0">Today</span>
                          <span className="text-amber-800 dark:text-amber-200 whitespace-nowrap">
                            {sh.isClosed ? 'Closed' : `${formatTime(sh.open!)} - ${formatTime(sh.close!)}`}
                          </span>
                        </div>
                        {sh.note && (
                          <span className="text-sm text-amber-700 dark:text-amber-300 italic">{sh.note}</span>
                        )}
                      </div>
                    ))}

                    {/* Upcoming Special Hours */}
                    {upcomingHours.map((sh, idx) => (
                      <div key={`upcoming-${sh.date}-${idx}`} className="flex flex-col gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-blue-900 dark:text-blue-100 flex-shrink-0">
                            {formatDate(sh.date)} {sh.daysAway && `(in ${sh.daysAway} day${sh.daysAway > 1 ? 's' : ''})`}
                          </span>
                          <span className="text-blue-800 dark:text-blue-200 whitespace-nowrap">
                            {sh.isClosed ? 'Closed' : `${formatTime(sh.open!)} - ${formatTime(sh.close!)}`}
                          </span>
                        </div>
                        {sh.note && (
                          <span className="text-sm text-blue-700 dark:text-blue-300 italic">{sh.note}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
